import React, { useState } from 'react';
import { FiImage, FiVideo, FiX } from 'react-icons/fi';
import MediaLibrary from './MediaLibrary';
import './MediaLibrary.css';

/**
 * MediaPicker - A button/field that opens the media library for selection
 *
 * Usage:
 * <MediaPicker
 *   value={selectedMedia}
 *   onChange={(media) => setSelectedMedia(media)}
 *   type="image"
 *   label="Featured Image"
 * />
 */
const MediaPicker = ({
  value,
  onChange,
  type = 'image', // 'image', 'video', 'any'
  multiple = false,
  label = 'Select Media',
  placeholder = 'Click to select media',
  className = '',
}) => {
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const handleSelect = (media) => {
    onChange(media);
    setIsLibraryOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleRemoveItem = (e, itemToRemove) => {
    e.stopPropagation();
    if (multiple && Array.isArray(value)) {
      onChange(value.filter(item => item.id !== itemToRemove.id));
    }
  };

  const acceptedTypes = type === 'any'
    ? ['image', 'video', 'document']
    : type === 'video'
      ? ['video']
      : ['image'];

  const renderPreview = () => {
    if (!value) return null;

    if (multiple && Array.isArray(value)) {
      return (
        <div className="media-picker-grid">
          {value.map(item => (
            <div key={item.id} className="media-picker-grid-item">
              {item.media_type === 'image' ? (
                <img src={item.thumbnail_url || item.url} alt={item.alt_text || ''} />
              ) : (
                <div className="media-picker-file">
                  <FiVideo size={24} />
                </div>
              )}
              <button
                className="media-picker-remove-item"
                onClick={(e) => handleRemoveItem(e, item)}
              >
                <FiX size={12} />
              </button>
            </div>
          ))}
        </div>
      );
    }

    // Single value
    return (
      <div className="media-picker-preview-single">
        {value.media_type === 'image' ? (
          <img src={value.thumbnail_url || value.url} alt={value.alt_text || ''} />
        ) : value.media_type === 'video' ? (
          <video src={value.url} />
        ) : (
          <div className="media-picker-file">
            <FiImage size={32} />
            <span>{value.filename}</span>
          </div>
        )}
        <div className="media-picker-preview-info">
          <span className="media-picker-filename">{value.filename}</span>
          {value.alt_text && (
            <span className="media-picker-alt">Alt: {value.alt_text}</span>
          )}
        </div>
        <button className="media-picker-clear" onClick={handleClear}>
          <FiX size={16} />
        </button>
      </div>
    );
  };

  const hasValue = multiple ? (Array.isArray(value) && value.length > 0) : !!value;

  return (
    <div className={`media-picker ${className}`}>
      {label && <label className="media-picker-label">{label}</label>}

      <div
        className={`media-picker-field ${hasValue ? 'has-value' : ''}`}
        onClick={() => setIsLibraryOpen(true)}
      >
        {hasValue ? (
          renderPreview()
        ) : (
          <div className="media-picker-placeholder">
            {type === 'video' ? <FiVideo size={24} /> : <FiImage size={24} />}
            <span>{placeholder}</span>
          </div>
        )}
      </div>

      {hasValue && !multiple && (
        <div className="media-picker-actions">
          <button
            type="button"
            className="media-picker-change"
            onClick={() => setIsLibraryOpen(true)}
          >
            Change
          </button>
          <button
            type="button"
            className="media-picker-remove"
            onClick={handleClear}
          >
            Remove
          </button>
        </div>
      )}

      <MediaLibrary
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelect={handleSelect}
        multiple={multiple}
        acceptedTypes={acceptedTypes}
        title={multiple ? 'Select Media' : `Select ${type === 'video' ? 'Video' : 'Image'}`}
      />
    </div>
  );
};

export default MediaPicker;
